import Phaser from 'phaser';
import protoMap from '../../data/maps/proto.json';
import { loadContent, type Content } from '../content';
import { loadMap } from '../content/maps';
import { GestureRecognizer } from '../input/gestures';
import { selectInBox, selectSameType, type Selectable, type WorldRect } from '../input/selection';
import { resolveTap, type Selection, type TapTarget } from '../input/tapToCommand';
import { TILE } from '../sim/core/fixed';
import { MS_PER_TICK, Sim } from '../sim/core/sim';
import { buildProgress, isComplete, type Building } from '../sim/entities/building';
import type { Unit } from '../sim/entities/unit';
import type { BuildingDef } from '../sim/data/types';
import { isForest } from '../sim/map/gameMap';
import { createHud, type Hud } from '../ui/hud';
import { railStateFor, type Names, type Placement, type RailAction } from '../ui/railState';
import { CameraModel } from './cameraModel';
import { TILE_PX } from './constants';

const PLAYER = 0;
const PLAYER_FACTION = 'orcs';
const FACTION_BY_OWNER: Record<number, string> = { 0: 'orcs', 1: 'elves' };
/** Taps within this many tiles of a unit's center select it (generous phone hit area). */
const UNIT_HIT_RADIUS_TILES = 0.6;

/** Converts simulation units to screen pixels at zoom 1. */
function unitsToPx(units: number): number {
  return (units * TILE_PX) / TILE;
}

function pxToUnits(px: number): number {
  return Math.round((px * TILE) / TILE_PX);
}

interface BuildingView {
  readonly building: Building;
  readonly box: Phaser.GameObjects.Rectangle;
  readonly label: Phaser.GameObjects.Text;
  readonly progress: Phaser.GameObjects.Rectangle;
  readonly hpBack: Phaser.GameObjects.Rectangle;
  readonly hpFill: Phaser.GameObjects.Rectangle;
  lastGold: number;
  lastProgress: number;
  lastHp: number | null;
}

/** Placement mode: the player is choosing where a building goes. */
interface Placing {
  readonly def: BuildingDef;
  /** Top-left footprint tile, or null until the first map tap. */
  at: { x: number; y: number } | null;
}

interface UnitView {
  readonly body: Phaser.GameObjects.Rectangle;
  readonly carryDot: Phaser.GameObjects.Arc;
  readonly hpBack: Phaser.GameObjects.Rectangle;
  readonly hpFill: Phaser.GameObjects.Rectangle;
}

/**
 * Draws the simulation and turns taps into commands. The sim never knows this exists.
 * Fixed-tick stepping with render interpolation (CLAUDE.md §4.2).
 */
export class GameScene extends Phaser.Scene {
  private sim!: Sim;
  private content!: Content;
  private hud!: Hud;
  private selection: Selection = null;
  private readonly unitViews = new Map<number, UnitView>();
  private readonly buildingViews = new Map<number, BuildingView>();
  private placing: Placing | null = null;
  private ghost!: Phaser.GameObjects.Rectangle;
  private ghostLabel!: Phaser.GameObjects.Text;
  private readonly selectionRings = new Map<number, Phaser.GameObjects.Rectangle>();
  private buildingRing!: Phaser.GameObjects.Rectangle;
  /** One-shot box-select: armed by the Select button, consumed by the next drag. */
  private boxMode = false;
  private box: { x0: number; y0: number; x1: number; y1: number } | null = null;
  private boxGfx!: Phaser.GameObjects.Graphics;
  private pathGfx: Phaser.GameObjects.Graphics | null = null;
  private terrainGfx!: Phaser.GameObjects.Graphics;
  /** Forest flags as last drawn, so cleared trees get redrawn as grass. */
  private forestSnapshot!: Uint8Array;
  private accumulatorMs = 0;
  private readonly gestures = new GestureRecognizer(8);
  private cameraModel!: CameraModel;
  /** Dev overlay (CLAUDE.md §10), on with `?debug=1`. */
  private readonly debug = new URLSearchParams(window.location.search).has('debug');
  private lastGesture = '(none yet)';
  private pointerLog = '';
  private lastTime: number | null = null;

  constructor() {
    super('game');
  }

  create(): void {
    this.content = loadContent();
    const loaded = loadMap(protoMap);
    this.sim = new Sim(loaded.map, 12345, {
      unitDefs: this.content.units,
      buildingDefs: this.content.buildings,
    });

    const start = loaded.starts[0];
    if (!start) throw new Error('map has no start position');
    const townCenter = this.sim.spawnBuilding(
      PLAYER,
      this.content.building('town-center'),
      start.x,
      start.y,
    );
    const spawnTile = this.sim.freeTileAround(townCenter);
    if (!spawnTile) throw new Error('no free tile around the town center');
    const worker = this.sim.spawnUnit(
      PLAYER,
      this.content.unit('worker'),
      spawnTile.x,
      spawnTile.y,
    );
    this.sim.player(PLAYER).supplyUsed += worker.def.supply;
    this.sim.player(PLAYER).gold = loaded.startingResources.gold;
    this.sim.player(PLAYER).lumber = loaded.startingResources.lumber;
    for (const m of loaded.mines)
      this.sim.spawnMine(this.content.building('gold-mine'), m.x, m.y, m.gold);
    for (const u of loaded.units) {
      const spawned = this.sim.spawnUnit(u.owner, this.content.unit(u.def), u.x, u.y);
      this.sim.player(u.owner).supplyUsed += spawned.def.supply;
    }
    this.selection = { kind: 'units', ids: [worker.id] };

    this.drawTerrain();
    this.buildingRing = this.add
      .rectangle(0, 0, TILE_PX, TILE_PX)
      .setStrokeStyle(2, 0xffffff, 0.9)
      .setDepth(9)
      .setVisible(false);
    this.boxGfx = this.add.graphics().setDepth(9);
    this.ghost = this.add
      .rectangle(0, 0, TILE_PX, TILE_PX, 0xffffff, 0.35)
      .setStrokeStyle(3, 0xffffff, 0.9)
      .setDepth(8)
      .setVisible(false);
    this.ghostLabel = this.add
      .text(0, 0, '', {
        fontSize: '22px',
        color: '#ffffff',
        fontFamily: 'sans-serif',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(8)
      .setVisible(false);

    this.hud = createHud({
      onAction: (a) => this.onRailAction(a),
      onSelect: () => this.toggleBoxMode(),
      onStop: () => this.stopSelected(),
    });
    this.cameraModel = new CameraModel(this.sim.map.width * TILE_PX, this.sim.map.height * TILE_PX);
    this.fitCamera();
    this.scale.on('resize', () => this.fitCamera());

    // Taps, pans, and pinches (CLAUDE.md §5). Taps fire on release so a drag never commands.
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.pointerLog = `down id=${p.id} (${p.x | 0},${p.y | 0})`;
      if (this.boxMode) {
        const w = this.cameraModel.toWorld(p.x, p.y, this.viewport());
        this.box = { x0: w.x, y0: w.y, x1: w.x, y1: w.y };
        return;
      }
      this.gestures.down({ id: p.id, x: p.x, y: p.y });
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      this.pointerLog = `move id=${p.id} (${p.x | 0},${p.y | 0})`;
      if (this.box) {
        const w = this.cameraModel.toWorld(p.x, p.y, this.viewport());
        this.box = { ...this.box, x1: w.x, y1: w.y };
        return;
      }
      this.applyGesture(this.gestures.move({ id: p.id, x: p.x, y: p.y }));
    });
    const up = (p: Phaser.Input.Pointer) => {
      if (this.box) {
        this.finishBox();
        return;
      }
      this.applyGesture(this.gestures.up({ id: p.id, x: p.x, y: p.y }));
    };
    this.input.on('pointerup', up);
    this.input.on('pointerupoutside', up);
    this.input.on('wheel', (p: Phaser.Input.Pointer, _objs: unknown, _dx: number, dy: number) => {
      this.cameraModel.zoomAt(dy < 0 ? 1.15 : 1 / 1.15, p.x, p.y, this.viewport());
      this.applyCamera();
    });

    if (import.meta.env.DEV) {
      // Hook for Playwright smoke tests and manual poking from the console.
      window.__tusk = {
        game: this.game,
        sim: this.sim,
        content: this.content,
        selectedIds: () => this.selectedIds(),
      };
      // First piece of the debug overlay (CLAUDE.md §10): show the selected unit's path.
      this.pathGfx = this.add.graphics().setDepth(5);
    }
  }

  override update(time: number): void {
    // Phaser's `delta` argument is smoothed toward the target frame time (fps.smoothStep),
    // so under a slow frame rate it under-reports and the simulation falls behind real time.
    // `time` is the true accumulated clock, so derive the step from it instead.
    const deltaMs = this.lastTime === null ? 0 : time - this.lastTime;
    this.lastTime = time;
    // Clamp so a backgrounded tab does not spiral through hundreds of ticks on resume.
    this.accumulatorMs += Math.min(deltaMs, 250);
    while (this.accumulatorMs >= MS_PER_TICK) {
      this.sim.step();
      this.accumulatorMs -= MS_PER_TICK;
    }
    const alpha = this.accumulatorMs / MS_PER_TICK;

    this.applyCamera();
    this.syncUnits(alpha);
    this.refreshTerrainIfChanged();
    this.syncBuildings();
    this.syncSelection();
    this.syncHud();
  }

  // ---- taps ----

  /** The map area: the canvas minus the command rail on the right. */
  private viewport(): { width: number; height: number } {
    return {
      width: Math.max(1, this.scale.width - this.hud.railWidth()),
      height: this.scale.height,
    };
  }

  private applyGesture(g: ReturnType<GestureRecognizer['move']>): void {
    if (!g) return;
    this.lastGesture =
      g.kind === 'tap' || g.kind === 'doubletap'
        ? `${g.kind} (${g.x | 0},${g.y | 0})`
        : g.kind === 'pan'
          ? `pan dx=${g.dx | 0} dy=${g.dy | 0}`
          : `pinch x${g.scale.toFixed(2)} at (${g.cx | 0},${g.cy | 0}) drift (${g.dx | 0},${g.dy | 0})`;
    if (g.kind === 'tap') {
      this.onTap(g.x, g.y);
      return;
    }
    if (g.kind === 'doubletap') {
      this.onDoubleTap(g.x, g.y);
      return;
    }
    const v = this.viewport();
    if (g.kind === 'pan') this.cameraModel.panBy(g.dx, g.dy, v);
    else {
      this.cameraModel.zoomAt(g.scale, g.cx, g.cy, v);
      this.cameraModel.panBy(g.dx, g.dy, v);
    }
    this.applyCamera();
  }

  /** Copy the model onto Phaser's camera. */
  private applyCamera(): void {
    const cam = this.cameras.main;
    const v = this.viewport();
    cam.setViewport(0, 0, v.width, v.height);
    cam.setZoom(this.cameraModel.zoom);
    cam.centerOn(this.cameraModel.centerX, this.cameraModel.centerY);
  }

  private onTap(sx: number, sy: number): void {
    const world = this.cameraModel.toWorld(sx, sy, this.viewport());
    const wx = pxToUnits(world.x);
    const wy = pxToUnits(world.y);
    const tx = Math.floor(world.x / TILE_PX);
    const ty = Math.floor(world.y / TILE_PX);

    if (this.placing) {
      // The tapped tile becomes the footprint's center.
      const { def } = this.placing;
      this.placing.at = { x: tx - Math.floor(def.width / 2), y: ty - Math.floor(def.height / 2) };
      return;
    }

    let target: TapTarget = { kind: 'ground' };
    const unit = this.unitNear(wx, wy);
    const building = this.sim.buildingAt(tx, ty);
    if (unit) target = { kind: 'unit', unitId: unit.id, own: unit.owner === PLAYER };
    else if (building) {
      target = {
        kind: 'building',
        buildingId: building.id,
        own: building.owner === PLAYER,
        mine: building.def.mine !== undefined,
        underConstruction: !isComplete(building),
      };
    } else if (isForest(this.sim.map, tx, ty)) target = { kind: 'forest', x: tx, y: ty };

    const result = resolveTap(this.selection, target, wx, wy, this.sim.tick);
    if (result.selection?.kind !== 'units') this.placing = null;
    this.selection = result.selection;
    for (const c of result.commands) this.sim.enqueue(c);
  }

  /** Double-tap an own unit: select every own unit of that type on screen (nearest first, capped). */
  private onDoubleTap(sx: number, sy: number): void {
    const world = this.cameraModel.toWorld(sx, sy, this.viewport());
    const unit = this.unitNear(pxToUnits(world.x), pxToUnits(world.y));
    if (!unit || unit.owner !== PLAYER) return;
    const units = this.selectables();
    const anchor = units.find((u) => u.id === unit.id);
    if (!anchor) return;
    const ids = selectSameType(units, anchor, this.viewRectUnits());
    this.selection = { kind: 'units', ids };
    this.placing = null;
  }

  /** Arm or disarm one-shot box selection (the HUD's Select button). */
  private toggleBoxMode(): void {
    this.boxMode = !this.boxMode;
    this.box = null;
  }

  private finishBox(): void {
    const b = this.box;
    this.box = null;
    this.boxMode = false;
    if (!b) return;
    const box: WorldRect = {
      x0: pxToUnits(b.x0),
      y0: pxToUnits(b.y0),
      x1: pxToUnits(b.x1),
      y1: pxToUnits(b.y1),
    };
    const ids = selectInBox(this.selectables(), box, PLAYER);
    this.selection = ids.length > 0 ? { kind: 'units', ids } : null;
    this.placing = null;
  }

  /** The sim's units in the shape the selection rules need (world units). */
  private selectables(): Selectable[] {
    return this.sim.units().map((u) => ({
      id: u.id,
      owner: u.owner,
      defId: u.def.id,
      x: u.x,
      y: u.y,
      selectable: !u.hidden && u.state.kind !== 'dying',
    }));
  }

  /** The visible part of the world, in simulation units. */
  private viewRectUnits(): WorldRect {
    const v = this.viewport();
    const tl = this.cameraModel.toWorld(0, 0, v);
    const br = this.cameraModel.toWorld(v.width, v.height, v);
    return { x0: pxToUnits(tl.x), y0: pxToUnits(tl.y), x1: pxToUnits(br.x), y1: pxToUnits(br.y) };
  }

  private selectedIds(): number[] {
    return this.selection?.kind === 'units' ? [...this.selection.ids] : [];
  }

  /** Nearest visible unit within the hit radius, by squared distance then id. */
  private unitNear(wx: number, wy: number): Unit | null {
    const r = UNIT_HIT_RADIUS_TILES * TILE;
    let best: Unit | null = null;
    let bestD = r * r;
    for (const u of this.sim.units()) {
      if (u.hidden || u.state.kind === 'dying') continue;
      const dx = u.x - wx;
      const dy = u.y - wy;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        best = u;
        bestD = d;
      }
    }
    return best;
  }

  // ---- per-frame sync ----

  private syncUnits(alpha: number): void {
    const seen = new Set<number>();
    for (const u of this.sim.units()) {
      seen.add(u.id);
      let view = this.unitViews.get(u.id);
      if (!view) {
        view = this.createUnitView(u);
        this.unitViews.set(u.id, view);
      }
      const x = unitsToPx(u.prevX + (u.x - u.prevX) * alpha);
      const y = unitsToPx(u.prevY + (u.y - u.prevY) * alpha);
      const dying = u.state.kind === 'dying';
      const fade = u.state.kind === 'dying' ? u.state.ticksLeft / 10 : 1;
      view.body.setPosition(x, y).setVisible(!u.hidden).setAlpha(fade);
      const hurt = !u.hidden && !dying && u.hp < u.def.hp;
      view.hpBack.setPosition(x, y - TILE_PX * 0.5).setVisible(hurt);
      view.hpFill
        .setPosition(x - TILE_PX * 0.3, y - TILE_PX * 0.5)
        .setSize(Math.max(1, (TILE_PX * 0.6 * Math.max(0, u.hp)) / u.def.hp), 3)
        .setVisible(hurt);
      view.carryDot
        .setPosition(x + TILE_PX * 0.22, y - TILE_PX * 0.22)
        .setVisible(!u.hidden && u.carrying !== null)
        .setFillStyle(u.carrying?.resource === 'lumber' ? 0x8a5a2b : 0xffd23f);
      if (this.selection?.kind === 'units' && this.selection.ids[0] === u.id)
        this.drawPath(u, x, y);
    }
    for (const [id, view] of this.unitViews) {
      if (!seen.has(id)) {
        view.body.destroy();
        view.carryDot.destroy();
        view.hpBack.destroy();
        view.hpFill.destroy();
        this.unitViews.delete(id);
      }
    }
    if (this.selection?.kind !== 'units') this.pathGfx?.clear();
  }

  private createUnitView(u: Unit): UnitView {
    // Shape and colour both differ per role (colour alone is not enough; see CLAUDE.md).
    const size = TILE_PX * 0.6;
    const body =
      u.def.id === 'melee'
        ? this.add
            .rectangle(unitsToPx(u.x), unitsToPx(u.y), size * 1.1, size * 1.1, 0xb23a3a)
            .setAngle(45)
        : u.def.id === 'ranged'
          ? this.add.rectangle(unitsToPx(u.x), unitsToPx(u.y), size * 1.2, size * 0.55, 0x2f6fb0)
          : this.add.rectangle(unitsToPx(u.x), unitsToPx(u.y), size, size, 0xe0b24c);
    const enemy = u.owner !== PLAYER;
    // Enemies: darker fill and a thick dark outline, so they read without relying on colour alone.
    if (enemy)
      body.setFillStyle(
        u.def.id === 'melee' ? 0x5c1f2e : u.def.id === 'ranged' ? 0x1d3f66 : 0x7a6224,
      );
    body.setStrokeStyle(enemy ? 4 : 2, enemy ? 0x120a0f : 0x3a2a10).setDepth(10);
    const hpBack = this.add
      .rectangle(0, 0, TILE_PX * 0.6, 3, 0x1b1408, 0.8)
      .setDepth(12)
      .setVisible(false);
    const hpFill = this.add
      .rectangle(0, 0, TILE_PX * 0.6, 3, 0x4caf50)
      .setOrigin(0, 0.5)
      .setDepth(13)
      .setVisible(false);
    const carryDot = this.add
      .circle(0, 0, TILE_PX * 0.14, 0xffd23f)
      .setStrokeStyle(1, 0x3a2a10)
      .setDepth(11)
      .setVisible(false);
    return { body, carryDot, hpBack, hpFill };
  }

  private syncBuildings(): void {
    const seen = new Set<number>();
    for (const b of this.sim.buildings()) {
      seen.add(b.id);
      let v = this.buildingViews.get(b.id);
      if (!v) {
        v = this.drawBuilding(b);
        this.buildingViews.set(b.id, v);
      }
      if (b.def.mine && b.gold !== v.lastGold) {
        v.lastGold = b.gold;
        v.label.setText(b.gold > 0 ? `Gold Mine\n${b.gold}` : 'Gold Mine\nempty');
        if (b.gold === 0) v.box.setFillStyle(0x6b6353);
      }
      const progress = buildProgress(b);
      if (progress !== v.lastProgress) {
        v.lastProgress = progress;
        const w = b.def.width * TILE_PX - 8;
        v.progress.setSize(Math.max(1, w * progress), 5).setVisible(progress < 1);
        v.box.setFillStyle(this.buildingColor(b), progress < 1 ? 0.35 : 1);
        v.label.setText(this.buildingName(b));
      }
      if (b.hp !== v.lastHp) {
        v.lastHp = b.hp;
        const max = b.def.hp ?? 0;
        const hurt = b.hp !== null && max > 0 && b.hp < max;
        const w = b.def.width * TILE_PX - 8;
        v.hpBack.setVisible(hurt);
        v.hpFill
          .setSize(Math.max(1, (w * Math.max(0, b.hp ?? 0)) / Math.max(1, max)), 5)
          .setVisible(hurt);
      }
    }
    for (const [id, v] of this.buildingViews) {
      if (!seen.has(id)) {
        v.box.destroy();
        v.label.destroy();
        v.progress.destroy();
        v.hpBack.destroy();
        v.hpFill.destroy();
        this.buildingViews.delete(id);
        if (this.selection?.kind === 'building' && this.selection.id === id) this.selection = null;
      }
    }
    this.syncGhost();
  }

  private syncGhost(): void {
    const p = this.placing;
    if (!p || !p.at) {
      this.ghost.setVisible(false);
      this.ghostLabel.setVisible(false);
      return;
    }
    const refusal = this.sim.canPlace(PLAYER, p.def, p.at.x, p.at.y);
    const w = p.def.width * TILE_PX;
    const h = p.def.height * TILE_PX;
    const cx = p.at.x * TILE_PX + w / 2;
    const cy = p.at.y * TILE_PX + h / 2;
    const color = refusal ? 0xd64545 : 0x4caf50;
    this.ghost
      .setPosition(cx, cy)
      .setSize(w, h)
      .setFillStyle(color, 0.35)
      .setStrokeStyle(3, color, 0.95)
      .setVisible(true);
    this.ghostLabel
      .setPosition(cx, cy)
      .setText(refusal ? '✕' : '✓')
      .setVisible(true);
  }

  private buildingColor(b: Building): number {
    if (b.def.mine) return b.gold > 0 ? 0xc9a227 : 0x6b6353;
    return b.def.id === 'barracks' ? 0x7a3b3b : 0x8a5a2b;
  }

  private buildingName(b: Building): string {
    if (b.def.mine) return `Gold Mine\n${b.gold}`;
    const factionId = FACTION_BY_OWNER[b.owner] ?? PLAYER_FACTION;
    const name = this.content.faction(factionId).buildingNames[b.def.id] ?? b.def.id;
    return isComplete(b)
      ? name
      : `${name}
(building)`;
  }

  private syncSelection(): void {
    // Prune units that died or vanished; rings for the rest.
    if (this.selection?.kind === 'units') {
      const alive = this.selection.ids.filter((id) => {
        const u = this.sim.getUnit(id);
        return u !== undefined && u.state.kind !== 'dying';
      });
      if (alive.length === 0) this.selection = null;
      else if (alive.length !== this.selection.ids.length)
        this.selection = { kind: 'units', ids: alive };
    }
    const wanted = new Set(this.selectedIds());
    for (const id of wanted) {
      const u = this.sim.getUnit(id);
      const view = u ? this.unitViews.get(u.id) : undefined;
      let ring = this.selectionRings.get(id);
      if (!ring) {
        ring = this.add
          .rectangle(0, 0, TILE_PX * 0.8, TILE_PX * 0.8)
          .setStrokeStyle(2, 0xffffff, 0.9)
          .setDepth(9);
        this.selectionRings.set(id, ring);
      }
      ring.setVisible(u !== undefined && view !== undefined && !u.hidden);
      if (view) ring.setPosition(view.body.x, view.body.y);
    }
    for (const [id, ring] of this.selectionRings) {
      if (!wanted.has(id)) {
        ring.destroy();
        this.selectionRings.delete(id);
      }
    }
    const sel = this.selection;
    const b = sel?.kind === 'building' ? this.sim.getBuilding(sel.id) : undefined;
    if (b) {
      const w = b.def.width * TILE_PX;
      const h = b.def.height * TILE_PX;
      this.buildingRing
        .setPosition(b.x * TILE_PX + w / 2, b.y * TILE_PX + h / 2)
        .setSize(w + 2, h + 2)
        .setVisible(true);
    } else {
      this.buildingRing.setVisible(false);
    }
    // The drag box, while one is being drawn.
    this.boxGfx.clear();
    if (this.box) {
      const x = Math.min(this.box.x0, this.box.x1);
      const y = Math.min(this.box.y0, this.box.y1);
      const w = Math.abs(this.box.x1 - this.box.x0);
      const h = Math.abs(this.box.y1 - this.box.y0);
      this.boxGfx.fillStyle(0xffffff, 0.15).fillRect(x, y, w, h);
      this.boxGfx.lineStyle(2, 0xffffff, 0.9).strokeRect(x, y, w, h);
    }
  }

  private syncHud(): void {
    const p = this.sim.player(PLAYER);
    this.hud.setResources(p.gold, p.lumber, p.supplyUsed, this.sim.supplyCap(PLAYER));
    this.hud.setRail(
      railStateFor(this.sim, PLAYER, this.selection, this.placement(), this.names()),
      this.boxMode,
    );
    if (this.debug) {
      const v = this.viewport();
      const c = this.cameraModel;
      this.hud.setDebug(
        [
          `tick ${this.sim.tick}  fps ${Math.round(this.game.loop.actualFps)}  units ${this.sim.units().length}`,
          `view ${v.width | 0}x${v.height | 0}  zoom ${c.zoom.toFixed(2)} (fit ${c.fitZoom(v).toFixed(2)})`,
          `center (${c.centerX | 0},${c.centerY | 0})  map ${c.mapWidth}x${c.mapHeight}`,
          `pointer ${this.pointerLog}`,
          `gesture ${this.lastGesture}`,
        ].join('\n'),
      );
    }
  }

  // ---- rail ----

  private names(): Names {
    const faction = this.content.faction(PLAYER_FACTION);
    return {
      unit: (id) => faction.unitNames[id] ?? id,
      building: (id) => faction.buildingNames[id] ?? id,
    };
  }

  private placement(): Placement | null {
    const p = this.placing;
    if (!p) return null;
    return {
      def: p.def,
      at: p.at,
      refusal: p.at ? this.sim.canPlace(PLAYER, p.def, p.at.x, p.at.y) : null,
    };
  }

  private stopSelected(): void {
    for (const unitId of this.selectedIds())
      this.sim.enqueue({ kind: 'stop', tick: this.sim.tick, unitId });
  }

  private onRailAction(a: RailAction): void {
    const tick = this.sim.tick;
    switch (a.kind) {
      case 'train':
        this.sim.enqueue({ kind: 'train', tick, buildingId: a.buildingId, unitDefId: a.unitDefId });
        break;
      case 'cancelTrain':
        this.sim.enqueue({ kind: 'cancelTrain', tick, buildingId: a.buildingId });
        break;
      case 'startPlacement': {
        const def = this.content.building(a.buildingDefId);
        this.placing = { def, at: null };
        break;
      }
      case 'confirmPlacement': {
        const p = this.placing;
        if (!p || !p.at) return;
        const builder = this.selectedIds()
          .map((id) => this.sim.getUnit(id))
          .find((u) => u !== undefined && u.def.builds.includes(p.def.id));
        if (builder) {
          this.sim.enqueue({
            kind: 'build',
            tick,
            unitId: builder.id,
            buildingDefId: p.def.id,
            x: p.at.x,
            y: p.at.y,
          });
        }
        this.placing = null;
        break;
      }
      case 'cancelPlacement':
        this.placing = null;
        break;
      case 'stopAll':
        this.stopSelected();
        break;
    }
  }

  // ---- drawing ----

  private drawBuilding(b: Building): BuildingView {
    const px = b.x * TILE_PX;
    const py = b.y * TILE_PX;
    const w = b.def.width * TILE_PX;
    const h = b.def.height * TILE_PX;
    const isMine = b.def.mine !== undefined;
    const complete = isComplete(b);
    const box = this.add
      .rectangle(px + w / 2, py + h / 2, w - 4, h - 4, this.buildingColor(b), complete ? 1 : 0.35)
      .setStrokeStyle(3, isMine ? 0x5c4a10 : 0x3a2a10)
      .setDepth(2);
    const label = this.add
      .text(px + w / 2, py + h / 2, this.buildingName(b), {
        fontSize: '13px',
        color: '#1b1408',
        fontFamily: 'sans-serif',
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(3);
    const progress = this.add
      .rectangle(px + 4, py + h - 8, 1, 5, 0xe0b24c)
      .setOrigin(0, 0)
      .setDepth(4)
      .setVisible(!complete);
    const hpBack = this.add
      .rectangle(px + 4, py + 4, w - 8, 5, 0x1b1408, 0.8)
      .setOrigin(0, 0)
      .setDepth(4)
      .setVisible(false);
    const hpFill = this.add
      .rectangle(px + 4, py + 4, 1, 5, 0x4caf50)
      .setOrigin(0, 0)
      .setDepth(5)
      .setVisible(false);
    return {
      building: b,
      box,
      label,
      progress,
      hpBack,
      hpFill,
      lastGold: b.gold,
      lastProgress: -1,
      lastHp: b.hp,
    };
  }

  private drawPath(unit: Unit, fromPx: number, fromPy: number): void {
    if (!this.pathGfx) return;
    this.pathGfx.clear();
    const path = unit.path;
    if (!path || unit.hidden) return;
    this.pathGfx.lineStyle(2, 0xfff3b0, 0.9);
    this.pathGfx.beginPath();
    this.pathGfx.moveTo(fromPx, fromPy);
    for (let i = path.index; i < path.waypoints.length; i++) {
      const wp = path.waypoints[i]!;
      this.pathGfx.lineTo(unitsToPx(wp.x), unitsToPx(wp.y));
    }
    this.pathGfx.strokePath();
    const end = path.waypoints[path.waypoints.length - 1]!;
    this.pathGfx.fillStyle(0xfff3b0, 0.9);
    this.pathGfx.fillCircle(unitsToPx(end.x), unitsToPx(end.y), 4);
  }

  private drawTerrain(): void {
    const { map } = this.sim;
    this.terrainGfx = this.add.graphics();
    this.forestSnapshot = new Uint8Array(map.width * map.height);
    this.redrawTerrain();
  }

  private redrawTerrain(): void {
    const { map } = this.sim;
    const gfx = this.terrainGfx;
    gfx.clear();
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const forest = isForest(map, x, y);
        this.forestSnapshot[y * map.width + x] = forest ? 1 : 0;
        gfx.fillStyle(forest ? 0x2f5d3a : (x + y) % 2 === 0 ? 0x7fae5c : 0x89b865, 1);
        gfx.fillRect(x * TILE_PX, y * TILE_PX, TILE_PX, TILE_PX);
        if (forest) {
          gfx.fillStyle(0x1f4128, 1);
          gfx.fillCircle(x * TILE_PX + TILE_PX / 2, y * TILE_PX + TILE_PX / 2, TILE_PX * 0.32);
        }
      }
    }
  }

  /** Redraw the terrain layer when any forest tile has been cleared since the last draw. */
  private refreshTerrainIfChanged(): void {
    const { map } = this.sim;
    for (let i = 0; i < this.forestSnapshot.length; i++) {
      const forest = (map.lumber[i] ?? 0) > 0 ? 1 : 0;
      if (forest !== this.forestSnapshot[i]) {
        this.redrawTerrain();
        return;
      }
    }
  }

  /** Fit the whole map on screen, centered. Camera pan/zoom gestures come later (#8). */
  /** Whole map on screen, centered: the starting view, and what a resize falls back to. */
  private fitCamera(): void {
    this.cameraModel.fit(this.viewport());
    this.applyCamera();
  }
}
